import { SCHOOL_STATUS_OPTIONS, type InstituteStatus } from '@/constants/institute';
import { cn } from '@/lib/utils';

export interface StatusFilterProps {
  /** The status being shown, or `''` for every status. */
  value: InstituteStatus | '';
  onChange: (value: InstituteStatus | '') => void;
  counts?: Partial<Record<InstituteStatus | 'ALL', number>>;
  className?: string;
}

/**
 * The lifecycle filter, in the open.
 *
 * It was a select inside a collapsed "Filters" drawer, which is the same as not
 * having it: "show me the suspended ones" is one of the two questions this
 * screen exists to answer, and it should not cost a click to discover. As chips
 * the current filter is also legible at a glance — a reader who followed
 * "Suspended: 3" from the dashboard can see why they are looking at three rows.
 *
 * Toggle buttons rather than tabs: this narrows a list that is already split by
 * kind above, and a second tablist on one screen reads as a second set of
 * pages.
 */
export function StatusFilter({ value, onChange, counts, className }: StatusFilterProps) {
  const options: { value: InstituteStatus | ''; label: string }[] = [
    { value: '', label: 'All statuses' },
    ...SCHOOL_STATUS_OPTIONS,
  ];

  return (
    <div role="group" aria-label="Filter by status" className={cn('flex flex-wrap gap-1.5', className)}>
      {options.map((option) => {
        const isSelected = option.value === value;
        const countKey = option.value || 'ALL';
        const count = counts?.[countKey];

        return (
          <button
            key={option.value || 'ALL'}
            type="button"
            aria-pressed={isSelected}
            onClick={() => onChange(option.value)}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm font-bold transition-all shadow-2xs',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-line',
              isSelected
                ? 'border-accent bg-accent text-white shadow-xs'
                : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:border-accent hover:text-accent',
            )}
          >
            <span>{option.label}</span>
            {count !== undefined && (
              <span
                className={cn(
                  'rounded-full px-1.5 py-0.5 text-xs font-black tabular-nums leading-none',
                  isSelected
                    ? 'bg-white/25 text-white'
                    : 'bg-slate-100 dark:bg-slate-700/80 text-slate-600 dark:text-slate-300',
                )}
              >
                {count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
